import { isNative } from "@/lib/capacitor";
import { checkNotificationPermission } from "@/lib/notifications";
import { getLanguage } from "@/i18n";
import { reportDevice } from "@/api/session";

import type { DeviceInfoInput } from "@mandoob/shared";

// What the app knows about the device it runs on, reported to the API
// silently (PUT /api/session/device): platform, model, OS version, app
// version, notification permission, UI language and locale region. Nothing is
// ever asked of the driver — whatever cannot be read stays undefined and the
// server keeps the previous value.
//
// The native shells load this site remotely, so an installed app may predate
// the Device plugin: every plugin call is guarded and falls back to parsing
// the user agent, which still yields the OS version and (on Android) the model.

/** Web bundle version, stamped by vite from package.json. */
declare const __APP_VERSION__: string;

let lastSent: string | null = null;

function platform(): DeviceInfoInput["platform"] {
  if (!isNative()) return "web";
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "ios" : "android";
}

/** Model + OS version from the UA when the Device plugin is unavailable. */
function fromUserAgent(): { model?: string; osVersion?: string } {
  const ua = navigator.userAgent;
  const android = /Android\s([\d.]+)(?:;\s*([^;)]+?))?(?:\s+Build|\))/i.exec(
    ua,
  );
  if (android) {
    const model = android[2]?.trim();
    return {
      osVersion: android[1],
      ...(model && !/^wv$/i.test(model) ? { model } : {}),
    };
  }
  const ios = /(?:iPhone|iPad|iPod).*?OS\s([\d_]+)/i.exec(ua);
  if (ios) {
    return {
      osVersion: ios[1]!.replace(/_/g, "."),
      model: /iPad/i.test(ua) ? "iPad" : "iPhone",
    };
  }
  const windows = /Windows NT\s([\d.]+)/i.exec(ua);
  if (windows) return { osVersion: `Windows ${windows[1]}` };
  const mac = /Mac OS X\s([\d_]+)/i.exec(ua);
  if (mac) return { osVersion: `macOS ${mac[1]!.replace(/_/g, ".")}` };
  return {};
}

async function deviceDetails(): Promise<{
  model?: string;
  osVersion?: string;
}> {
  try {
    const { Device } = await import("@capacitor/device");
    const info = await Device.getInfo();
    const model = [info.manufacturer, info.model]
      .filter((part) => part && part !== "unknown")
      .join(" ")
      .trim();
    return {
      ...(model ? { model } : {}),
      ...(info.osVersion ? { osVersion: info.osVersion } : {}),
    };
  } catch {
    // Plugin missing from an older shell (or no bridge at all): best effort.
    return fromUserAgent();
  }
}

async function appVersion(): Promise<string> {
  if (isNative()) {
    try {
      const { App } = await import("@capacitor/app");
      const info = await App.getInfo();
      return info.build ? `${info.version} (${info.build})` : info.version;
    } catch {
      // Fall through to the web bundle version.
    }
  }
  return __APP_VERSION__;
}

function region(): string | undefined {
  try {
    const locale = new Intl.Locale(navigator.language);
    return locale.region ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Collects and sends the report. Skipped when nothing changed since the last
 * send in this session, so callers can fire it freely (launch, resume, after
 * the permission prompt, after a language switch). Never throws.
 */
export async function reportDeviceInfo(): Promise<void> {
  try {
    const [details, version, pushPermission] = await Promise.all([
      deviceDetails(),
      appVersion(),
      checkNotificationPermission(),
    ]);
    const report: DeviceInfoInput = {
      platform: platform(),
      ...details,
      appVersion: version,
      pushPermission,
      language: getLanguage(),
      ...(region() ? { region: region() } : {}),
    };
    const key = JSON.stringify(report);
    if (key === lastSent) return;
    await reportDevice(report);
    lastSent = key;
  } catch {
    // Offline or signed out — the next launch reports again.
  }
}
